<template>
  <div>
    <component
      :is="customComponentName"
      v-if="customComponentName"
      :form-model="formModel"
      :root-form-model="rootFormModel"
      :required-data="requiredData"
      :view-mode="viewMode"
    ></component>
    <v-row v-else>
      <template v-for="(field, index) in fields">
        <template v-if="isGridField(field.typeId)">
          <v-col
            v-if="executeConditionAndResetIfNeeded(field)"
            :key="index"
            cols="12"
          >
            <grids
              v-validate="field.validations"
              :title="field.label"
              :value.sync="formModel[field.name]"
              :root-form-model="{ ...formModel, ...rootFormModel }"
              :data-sources="computedDataSources"
              :config="field.dataGridConfig"
              :required-data="requiredData"
              :view-mode="viewMode || executeStringWithRootContext(field.disabledIf)"
              :error-message="errors.first(field.label)"
            ></grids>
          </v-col>
        </template>
        <template v-else-if="field.isCustom">
          <component
            :is="field.customComponentName"
            v-if="executeConditionAndResetIfNeeded(field)"
            :key="index"
            :value.sync="formModel[field.name]"
            :technical-name="field.name"
            :label="field.label"
            :form-model="formModel"
            :root-form-model="{ ...formModel, ...rootFormModel }"
            :validates="field.validations"
            :column-size="field.column"
            :fields="field.customComponentFields"
            :hide-naj-box="hideNajBox"
            :view-mode="viewMode"
            :data-vv-as="field.label"
            :required-data="requiredData"
          ></component>
        </template>
        <template v-else-if="isSectionField(field.typeId)">
          <naj-box
            v-if="executeConditionAndResetIfNeeded(field)"
            :key="index"
            :no-header="!!field.formId || hideNajBox"
            :no-gutters="hideNajBox"
            :no-margin-bottom="!!field.formId || hideNajBox"
            :title="field.label"
          >
            <naj-dynamic-form
              :form-model="formModel"
              :root-form-model="rootFormModel"
              :required-data="requiredData"
              :form-fields="field.fields"
              :custom-component-name="field.customComponentName"
              :view-mode="viewMode"
              :readonly="readonly"
              v-on="$listeners"
            ></naj-dynamic-form>
          </naj-box>
        </template>
        <template v-else-if="isOtherField(field.typeId)">
          <v-col
            v-if="executeConditionAndResetIfNeeded(field)"
            :key="field.id"
            :cols="field.column"
            :align-self="isButton(field.typeId) ? 'center' : null"
          >
            <naj-dynamic-button
              v-if="isButton(field.typeId)"
              :outlined="field.outlined"
              :label="field.label"
              :form-model="formModel"
              :root-form-model="{ ...formModel, ...rootFormModel }"
              :click-callback="field.onClick"
              :readonly="readonly"
            ></naj-dynamic-button>
            <naj-map
              v-if="isMap(field.typeId)"
              v-validate="field.validations"
              :title="field.label"
              :value.sync="formModel[field.name]"
              :error-message="errors.first(field.label)"
            ></naj-map>
          </v-col>
        </template>
        <template v-else>
          <naj-dynamic-field
            :key="index"
            :field="field"
            :form-model="formModel"
            :root-form-model="rootFormModel"
            :data-sources="computedDataSources"
            :required-data="requiredData"
            :view-mode="viewMode"
            :readonly="readonly"
            @addOtherOption="addOtherOption"
            @hasOtherOptionSelected="hasOtherOptionSelected"
          ></naj-dynamic-field>
        </template>
      </template>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios';
import { convertToValidTechnicalName, deepClone, executeString } from '@/helpers/utils';
import DemoCustomFormComponents from '@/modules/global/components/demo/custom-form-components.js';
import CustomFormComponents from '@/modules/global/components/dynamic-form/custom-components/index';

export default {
  name: 'NajDynamicForm',
  inject: ['$validator'],
  components: {
    Grids: () => import('@/components/shared/grids'),
    NajDynamicButton: () => import('@/components/shared/naj-dynamic-button'),
    NajMap: () => import('@/components/shared/naj-map'),
    ...CustomFormComponents.reduce((componentsObj, item) => {
      componentsObj[item.value] = item.component;
      return componentsObj;
    }, {}),
    ...DemoCustomFormComponents,
  },
  props: {
    formModel: {
      type: Object,
      default: () => ({}),
    },
    rootFormModel: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    formFields: {
      type: Array,
      default: () => [],
    },
    dataSources: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    viewMode: {
      type: Boolean,
      default: false,
    },
    hideNajBox: {
      type: Boolean,
      default: false,
    },
    customComponentName: {
      type: String,
      required: false,
    },
    requiredData: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    readonly: {
      type: Boolean,
      default: undefined,
      required: false,
    },
  },
  data() {
    return {
      formFieldsExternalDataSourceLoaded: false,
      executeString,
      fetchedDataSources: {},
      debounceTimeout: false,
    };
  },
  computed: {
    fields() {
      const returnValue = this.prepareFields(this.formFields);
      this.updateFormFieldsExternalDataSourceLoaded(true);
      return returnValue;
    },
    computedDataSources() {
      return {
        ...this.fetchedDataSources,
        ...this.dataSources,
      };
    },
  },
  watch: {
    formModel: {
      handler(newVal, oldVal) {

        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => {
          this.fields.filter(f => f.typeId == this.formFieldType.formula)
            .forEach((formulaField) => {
              const properties = JSON.parse((formulaField.properties || '').replace(/['']+/g, `'`));
              const handlebarsVal = this.handlebars(properties.formula, this.formModel);
              if (handlebarsVal !== oldVal[formulaField.name])
                this.$set(this.formModel, formulaField.name, handlebarsVal);
            });
          this.$emit('change');
        }, 200);
      },
      deep: true,
    },
  },
  methods: {
    prepareFields(fields) {
      return fields.map((f) => {
        const fieldProperties = JSON.parse(f.properties || '{}');
        const externalDataSource =
          fieldProperties.externalDataSource &&
            Object.keys(fieldProperties.externalDataSource).length
            ? fieldProperties.externalDataSource
            : null;
        const internalDataSource =
          fieldProperties.internalDataSource &&
            Object.keys(fieldProperties.internalDataSource).length
            ? fieldProperties.internalDataSource
            : null;
        const cascade = fieldProperties.cascade;
        const showDescriptionTooltip = fieldProperties.showDescriptionTooltip;
        const dataSource = fieldProperties.dataSource || f.dataSource;
        !this.formFieldsExternalDataSourceLoaded && this.extractDataFromExternalAPI(
          `${f.id}${f.name}`,
          externalDataSource,
          cascade
        );
        const items = cascade && dataSource
          ? dataSource[this.formModel[cascade] || this.rootFormModel[cascade]]
          : dataSource;
        const isLTR = fieldProperties.isLTR;
        const ifVal = fieldProperties.if;
        const disabledIf = fieldProperties.disabledIf;
        const isDimmed = fieldProperties.isDimmed;
        const show = fieldProperties.show;
        const textName = fieldProperties.textName;
        const column = f.columnSize || fieldProperties.columnSize;
        let validations = fieldProperties.validations || {};
        const externalValidation = fieldProperties.externalValidation;
        const dateConfig = { ...(fieldProperties.dateConfig || {}) };
        const hideHijriDate = fieldProperties.hideHijriDate;
        if (externalValidation && externalValidation.endpoint) {
          const validationDetails = [];
          validationDetails.push(`${externalValidation.requestType || 'get'}`);
          validationDetails.push(`${externalValidation.module}`);
          validationDetails.push(`${externalValidation.endpoint}`);
          validationDetails.push(`${externalValidation.expectedStatus}`);
          validationDetails.push(`${externalValidation.expectedResults}`);
          validations.external_validation = validationDetails.join(',');
        }
        const onMounted = fieldProperties.mounted;
        const onInput = fieldProperties.input;
        const onChange = fieldProperties.change;
        const onClick = fieldProperties.click;
        const outlined = fieldProperties.outlined;
        const hasExternalEffect = fieldProperties.hasExternalEffect;
        const maxLength = fieldProperties.maxLength;
        if (maxLength) {
          if (typeof validations === 'string') validations += `max:${maxLength}`;
          else if (typeof validations === 'object') validations['max'] = maxLength;
        }
        const itemText = fieldProperties.itemText || 'name';
        const itemValue = fieldProperties.itemValue || 'id';
        const itemDescription = fieldProperties.itemDescription || 'description';
        const itemTooltip = fieldProperties.itemTooltip || 'tooltip';
        const itemIcon = fieldProperties.itemIcon || 'icon';
        const customComponentName = fieldProperties.customComponentName;
        const customComponentFields = {
          ...fieldProperties.customComponentFields,
          suggestedTechnicalName: fieldProperties.suggestedTechnicalName,
        };
        const isCustom = fieldProperties.isCustom;
        const formId = fieldProperties.formId;

        if (this.formModel[f.name] === undefined && fieldProperties.initialValue != undefined && !this.formModel[f.name + '_HasInitialValue']) {
          const initialValue = f.typeId === this.formFieldType.multipleSelectBox ?
            [fieldProperties.initialValue] : fieldProperties.initialValue;
          this.$set(this.formModel, f.name, initialValue);
          this.$set(this.formModel, f.name + '_HasInitialValue', true);
        }
        const field = {
          ...f,
          fields: f.fields ? this.prepareFields(f.fields) : [],
          if: ifVal ? ifVal.toString()
            .replace(/formModel/g, 'obj') : true,
          disabledIf: disabledIf
            ? disabledIf.toString()
              .replace(/formModel/g, 'obj')
            : false,
          isDimmed: isDimmed
            ? isDimmed.toString()
              .replace(/formModel/g, 'obj')
            : false,
          show: show ? show.toString()
            .replace(/formModel/g, 'obj') : true,
          isLTR,
          textName,
          dateConfig,
          dataGridConfig: {
            ...(fieldProperties.dataGridConfig || {}),
            gridId: fieldProperties.gridId,
          },
          column:
            (column || this.defaultColumnSize(f.typeId)) +
            (this.hasOtherOptionSelected(f)
              ? this.defaultColumnSize(this.formFieldType.text)
              : 0),
          externalDataSource,
          internalDataSource,
          validations: this.evalFieldValidations(validations),
          items: f.typeId === this.formFieldType.selectbox || f.typeId === this.formFieldType.multipleSelectBox
            ? this.getDataSourceWithOtherValues(items, this.formModel[f.name], itemValue, itemText)
            : items,
          maxLength,
          itemText,
          itemValue,
          itemDescription,
          itemTooltip,
          itemIcon,
          outlined,
          showDescriptionTooltip,
          customComponentName,
          customComponentFields,
          isCustom,
          formId,
          hideHijriDate,
          onMounted: onMounted ? (value) => {
            this.$nextTick(() => {
              if (typeof onMounted === 'function')
                onMounted(this.formModel, value); // not working yet
              else
                this.executeStringWithContext(onMounted);
            });
          } : null,
          onInput: (value) => {
            if (typeof onInput === 'function')
              onInput(this.formModel, value); // not working yet
            else
              this.executeStringWithContext(onInput);
            if (hasExternalEffect) {
              this.$emit('externalEffectEvent', {
                name: field.name,
                value: this.formModel[field.name],
              });
            }
            this.extractDataFromExternalAPIWhenDependenciesChange(f.name);
          },
          onChange: (value) => {
            if (typeof onChange === 'function')
              onChange(this.formModel, value); // not working yet
            else
              this.executeStringWithContext(onChange);
            if (hasExternalEffect) {
              this.$emit('externalEffectEvent', {
                name: field.name,
                value: this.formModel[field.name],
              });
            }
            this.extractDataFromExternalAPIWhenDependenciesChange(f.name);
          },
          onClick: onClick ? (value) => {
            if (typeof onClick === 'function')
              return onClick(this.formModel, value); // not working yet
            else
              return this.executeStringWithContext(onClick);
          } : null,
        };
        return field;
      });
    },
    isFieldsSection(typeId) {
      return this.formFieldType.fieldsSection == typeId;
    },
    updateFormFieldsExternalDataSourceLoaded(value) {
      this.formFieldsExternalDataSourceLoaded = value;
    },
    isGridField(fieldTypeId) {
      return fieldTypeId == this.formFieldType.dataGrid;
    },
    isButton(fieldTypeId) {
      return fieldTypeId == this.formFieldType.button;
    },
    isMap(fieldTypeId) {
      return fieldTypeId == this.formFieldType.map;
    },
    isOtherField(fieldTypeId) {
      return this.isButton(fieldTypeId) || this.isMap(fieldTypeId);
    },
    resetNestedFieldsValues(fields) {
      (fields || []).forEach((f) => {
        setTimeout(() => {
          if (this.formModel[f.name] !== undefined)
            this.$set(this.formModel, f.name, undefined);
          if (f.textName && this.formModel[f.textName] !== undefined)
            this.$set(this.formModel, f.textName, undefined);
        }, 50);
        this.resetNestedFieldsValues(f.fields);
      });
    },
    executeConditionAndResetIfNeeded(field) {
      var condition = !!this.executeStringWithRootContext(field.if);
      if (!condition && ((this.formModel || {}).shown || {})[convertToValidTechnicalName(`${field.name}_${field.id}`)] !== undefined && ((this.formModel || {}).shown || {})[convertToValidTechnicalName(`${field.name}_${field.id}`)] !== condition) {
        this.resetNestedFieldsValues(field.fields);
        setTimeout(() => {
          if (this.formModel[field.name] !== undefined)
            this.$set(this.formModel, field.name, undefined);
          if (field.textName && this.formModel[field.textName] !== undefined)
            this.$set(this.formModel, field.textName, undefined);
        }, 50);
      }
      if (!(this.formModel || {}).shown) (this.formModel || {}).shown = {};
      this.$set(this.formModel.shown, convertToValidTechnicalName(`${field.name}_${field.id}`), condition);
      return condition;
    },
    isSectionField(fieldTypeId) {
      return fieldTypeId == this.formFieldType.fieldsSection;
    },
    defaultColumnSize(fieldTypeId) {
      return fieldTypeId == this.formFieldType.date ? 6 : 4;
    },
    getDataSourceWithOtherValues(dataSource, otherValue, itemValue, itemText) {
      if (Array.isArray(dataSource)) {
        if (Array.isArray(otherValue)) {
          return dataSource.concat(otherValue.filter(cv => !dataSource.some(ds => typeof ds === 'object' ? ds[itemValue] : ds === typeof cv === 'object' ? cv[itemValue] : cv)));
        }
        if (typeof otherValue === 'string') {
          return dataSource.concat([{
            [itemValue]: otherValue,
            [itemText]: otherValue,
          }]);
        }
      }
      return dataSource;
    },
    hasOtherOptionSelected(field) {
      if (
        field.typeId === this.formFieldType.multipleSelectBox &&
        this.formModel[field.name] && Array.isArray(this.formModel[field.name]) &&
        this.formModel[field.name].some(r => +r === -1)
      ) {
        return true;
      }
      if (
        field.typeId === this.formFieldType.selectbox &&
        this.formModel[field.name] == -1
      ) {
        return true;
      }
      return false;
    },
    addOtherOption(field) {
      if (!this.formModel[field.name]) return;
      if (
        Array.isArray(this.formModel[field.name]) &&
        field.typeId === this.formFieldType.multipleSelectBox
      ) {
        this.formModel[field.name] = this.formModel[field.name].filter(
          v => +v !== -1
        );
        this.$push(
          this.formModel[field.name],
          this.formModel[`${field.name}-other`]
        );
      }
      if (field.typeId === this.formFieldType.selectbox) {
        this.$set(
          this.formModel,
          field.name,
          this.formModel[`${field.name}-other`]
        );
      }
      delete this.formModel[`${field.name}-other`];
    },
    extractDataFromExternalAPI(prop, config, cascade) {
      if (
        config &&
        (!cascade || (cascade && (this.formModel[cascade] || this.rootFormModel[cascade])))
      ) {
        const modulePrefix = config.module || '';
        const params = {};
        if (config.params) {
          for (const key in config.params) {
            // eslint-disable-next-line no-prototype-builtins
            if (config.params.hasOwnProperty(key)) {
              const param = config.params[key];
              params[param] = this.formModel[param] || this.rootFormModel[param];
            }
          }
        }
        if (cascade && (this.formModel[cascade] || this.rootFormModel[cascade])) {
          params[cascade] = this.formModel[cascade] || this.rootFormModel[cascade];
          params['query'] = {
            filters: [{
              field: cascade,
              value: params[cascade],
            }],
          };
        }
        if (
          !this.computedDataSources[prop] ||
          !this.computedDataSources[prop].length
        ) {
          axios[config.requestType || 'get'](`${modulePrefix}${config.endpoint}`, { params })
            .then((d) => {
              let data = !!config.isPagedList && !!d.data ? d.data.list : d.data;
              this.$set(this.fetchedDataSources, prop, data);
            });
        }
      }
    },
    extractDataFromExternalAPIWhenDependenciesChange(currentFieldName) {
      this.flatFormFields(this.formFields)
        .filter((f) => {
          const fieldProperties = JSON.parse(f.properties || '{}');
          return fieldProperties.cascade === currentFieldName
            || ((fieldProperties.externalDataSource || {}).params || []).some(p => p === currentFieldName);
        })
        .forEach((f) => {
          this.$set(this.formModel, f.name, undefined);
          this.$set(this.fetchedDataSources, `${f.id}${f.name}`, []);
          const fieldProperties = JSON.parse(f.properties || '{}');
          this.extractDataFromExternalAPI(
            `${f.id}${f.name}`,
            fieldProperties.externalDataSource,
            fieldProperties.cascade
          );
        });
    },
    evalFieldValidations(notEvaluatedValidations) {
      return Object.keys(notEvaluatedValidations)
        .reduce((validations, key) => {
          const params = notEvaluatedValidations[key];
          if (params && typeof params === 'string') {
            // eslint-disable-next-line no-param-reassign
            validations[key] = params
              .replace(/formModel/g, 'obj')
              .split(',')
              .map(param => this.executeStringWithRootContext(param));
          } else {
            // eslint-disable-next-line no-param-reassign
            validations[key] = notEvaluatedValidations[key] || true;
          }
          return validations;
        }, {});
    },
    executeStringWithContext(params) {
      return this.executeString(params, this.formModel);
    },
    executeStringWithRootContext(params) {
      return this.executeString(params, {
        ...this.requiredData,
        ...this.rootFormModel,
        ...this.formModel,
        $set: this.$set,
        $push: this.$push,
        $delete: this.$delete,
      });
    },
    flatFormFields(fields) {
      const flatFields = deepClone(fields || []);
      const childFields = [];
      flatFields.forEach((f) => {
        const fields = f.fields || (JSON.parse(f.properties || '{}').dataGridConfig || {}).fields;
        if ((fields || []).length)
          childFields.push(...this.flatFormFields(fields));
      });
      return flatFields.concat(childFields);
    },
  },
};
</script>

<style lang="scss" scoped>
.with-other-option {
  width: 50%;
  display: inline-block;
  &.other-option-box {
    padding-right: 24px;
    .other-option {
      display: inline-block;
    }
  }
}

::v-deep .dimmed {
  pointer-events: none !important;
  opacity: 0.5;
}
</style>
